import {
  HTMLAttributes,
  forwardRef,
  useState,
  createContext,
  useContext,
  ReactNode,
  DetailedHTMLProps,
  ImgHTMLAttributes,
  ButtonHTMLAttributes,
} from 'react';
import { twMerge } from 'tailwind-merge';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { Tooltip, TooltipProps, BaseIcon, BaseIconProps } from '@scale/scaleui';
import { Popover, Transition } from '@headlessui/react';
import { usePopper } from 'react-popper';
import { Options } from '@popperjs/core';
import isUndefined from 'lodash/isUndefined';
import { isFunction } from 'lodash';

type ButtonTooltipProps = Omit<TooltipProps, 'target' | 'text'>;

interface HeaderButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  selected?: boolean;
  tooltip?: string;
  tooltipProps?: ButtonTooltipProps;
  className?: string;
  disabled?: boolean;
  icon: FontAwesomeIconProps['icon'];
  iconProps?: BaseIconProps['iconProps'];
  transform?: FontAwesomeIconProps['transform'];
  mask?: FontAwesomeIconProps['mask'];
  badge?: ReactNode;
  enableDisabledTooltip?: boolean;
  ['data-testid']?: string;
}

const HeaderButton = forwardRef<HTMLButtonElement, HeaderButtonProps>((props, ref) => {
  const {
    badge,
    disabled,
    onClick,
    icon,
    selected,
    tooltip,
    tooltipProps,
    className,
    iconProps,
    enableDisabledTooltip,
  } = props;
  const target = (
    <button
      ref={ref}
      disabled={disabled}
      onClick={onClick}
      className={twMerge(
        'scaleui transition-all relative border-none focus:outline-none focus:ring-0 flex items-center justify-center rounded w-6 h-6 focus:bg-neutral-300',
        selected && 'focus:bg-inherit bg-neutral-200',
        !selected && 'hover:bg-neutral-200 ',
        disabled && 'cursor-not-allowed hover:bg-transparent',
        className,
      )}
      data-testid={props['data-testid']}
    >
      <BaseIcon
        icon={icon}
        variant="neutral"
        size="sm"
        disabled={disabled}
        contrastText={selected}
        iconProps={iconProps}
      />
      {badge && (
        <div className="absolute inline-flex items-center justify-center w-5 h-5 text-xs text-neutral-900 bg-primary-100 border border-neutral-0 rounded-full -top-2 -right-2">
          {badge}
        </div>
      )}
    </button>
  );

  return tooltip ? (
    <Tooltip
      text={tooltip}
      target={target}
      disabled={disabled && !enableDisabledTooltip}
      delayDuration={0}
      {...tooltipProps}
    />
  ) : (
    target
  );
});

export const StepCardContext = createContext<{
  cardReference: HTMLDivElement | null;
  readOnly: boolean;
}>({
  cardReference: null,
  readOnly: false,
});

interface StepCardComponentProps extends HTMLAttributes<HTMLDivElement> {
  readOnly?: boolean;
}

const StepCardComponent = (props: StepCardComponentProps) => {
  const { className, children, readOnly = false } = props;
  const [cardReference, setCardReference] = useState<HTMLDivElement | null>(null);
  return (
    <div
      data-testid="step-card"
      {...props}
      ref={setCardReference}
      className={twMerge(
        'w-full text-left border border-neutral-200 rounded-lg h-fit scaleui bg-neutral-0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-400 transition-colors max-w-screen-xl',
        className,
      )}
    >
      <StepCardContext.Provider value={{ cardReference, readOnly }}>
        {children}
      </StepCardContext.Provider>
    </div>
  );
};

interface StepCardHeaderProps extends HTMLAttributes<HTMLDivElement> {}

const StepCardHeader = (props: StepCardHeaderProps) => {
  const { className } = props;
  return (
    <div
      {...props}
      className={twMerge(
        'flex items-center justify-between w-full bg-neutral-50 rounded-t-lg py-2 px-4',
        className,
      )}
    />
  );
};

interface StepCardSubHeaderRowProps extends HTMLAttributes<HTMLDivElement> {}

const StepCardSubHeaderRow = (props: StepCardSubHeaderRowProps) => {
  const { className } = props;
  return (
    <div {...props} className={twMerge('flex w-full divide-x divide-neutral-200', className)} />
  );
};

interface StepCardSubHeaderProps extends HTMLAttributes<HTMLDivElement> {}

const StepCardSubHeader = (props: StepCardSubHeaderProps) => {
  const { className } = props;
  return (
    <div
      {...props}
      className={twMerge(
        'flex items-center justify-between w-full bg-neutral-50 px-4 py-2',
        className,
      )}
    />
  );
};

type StepCardSubHeaderTitleProps = HTMLAttributes<HTMLDivElement>;

const StepCardSubHeaderTitle = (props: StepCardSubHeaderTitleProps) => {
  const { className } = props;
  return (
    <div {...props} className={twMerge('w-full text-neutral-600 font-medium text-xs', className)} />
  );
};

interface StepCardHeaderLeftProps extends HTMLAttributes<HTMLDivElement> {}

const StepCardHeaderLeft = (props: StepCardHeaderLeftProps) => {
  const { className } = props;
  return <div {...props} className={twMerge('flex items-center w-full gap-3', className)} />;
};

const StepCardHeaderColumn = (props: HTMLAttributes<HTMLDivElement>) => {
  const { className } = props;
  return <div {...props} className={twMerge('w-full flex flex-col gap-[2px]', className)} />;
};

interface StepCardHeaderRightProps extends HTMLAttributes<HTMLDivElement> {}

const StepCardHeaderRight = (props: StepCardHeaderRightProps) => {
  const { className, children } = props;
  const { readOnly } = useContext(StepCardContext);

  return (
    <div {...props} className={twMerge('flex ml-2 gap-2 items-center', className)}>
      {readOnly && <span className="text-xs text-neutral-500 whitespace-nowrap">Read-only</span>}
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
};
interface StepCardHeaderIconProps extends FontAwesomeIconProps {}

const StepCardHeaderIcon = (props: StepCardHeaderIconProps) => {
  const { className } = props;
  return (
    <div
      className={twMerge(
        'flex items-center justify-center rounded-full bg-neutral-0 border border-neutral-200 text-md text-neutral-500 w-8 h-8',
        className,
      )}
    >
      <FontAwesomeIcon {...props} />
    </div>
  );
};

type StepCardHeaderImageProps = DetailedHTMLProps<
  ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
>;

const StepCardHeaderImage = (props: StepCardHeaderImageProps) => {
  const { className, ...imgProps } = props;
  return (
    <div
      className={twMerge(
        'flex items-center justify-center rounded-full bg-neutral-0 border border-neutral-200 text-md text-neutral-500 w-8 h-8',
        className,
      )}
    >
      <img {...imgProps} className="rounded-full" />
    </div>
  );
};

type StepCardHeaderSuperscriptProps = HTMLAttributes<HTMLDivElement>;

const StepCardHeaderSuperscript = (props: StepCardHeaderSuperscriptProps) => {
  const { className } = props;
  return (
    <div
      {...props}
      className={twMerge('w-full text-neutral-600 font-normal text-[10px]', className)}
    />
  );
};

type StepCardHeaderTitleProps = HTMLAttributes<HTMLDivElement>;

const StepCardHeaderTitle = (props: StepCardHeaderTitleProps) => {
  const { className } = props;
  return (
    <div
      {...props}
      className={twMerge('w-full text-neutral-600 font-semibold text-base', className)}
    />
  );
};

type StepCardHeaderSubtitleProps = HTMLAttributes<HTMLDivElement>;

const StepCardHeaderSubtitle = (props: StepCardHeaderSubtitleProps) => {
  const { className } = props;
  return (
    <div {...props} className={twMerge('w-full text-neutral-600 font-normal text-xs', className)} />
  );
};

interface StepCardBodyProps extends HTMLAttributes<HTMLDivElement> {}

const StepCardBody = (props: StepCardBodyProps) => {
  const { className } = props;
  return <div {...props} className={twMerge('py-2 px-4 border-t border-neutral-100', className)} />;
};

interface StepCardFooterProps extends HTMLAttributes<HTMLDivElement> {}

const StepCardFooter = (props: StepCardFooterProps) => {
  const { className } = props;
  return (
    <div
      {...props}
      className={twMerge(
        'flex items-center w-full px-4 py-3 border-t border-neutral-100',
        className,
      )}
    />
  );
};

type StepCardPopoverProps = HeaderButtonProps & {
  children?:
    | ReactNode
    | (({
        close,
        buttonReference,
        panelReference,
        referenceElement,
      }: {
        close: () => void;
        buttonReference: HTMLButtonElement | null;
        panelReference: HTMLDivElement | null;
        referenceElement: HTMLElement | null;
      }) => ReactNode);
  placement?: Options['placement'];
  modifiers?: Options['modifiers'];
  referenceElement?: HTMLElement | null;
  useStepCardReference?: boolean;
  isOpen?: boolean;
  // Useful if we want to re-use the majority of the step
  // but manage visibility of a popover using a separate component.
  // We need to be wary of overloading these props though.
  disablePopoverButton?: boolean;
  onClose?: () => void;
};

const StepCardPopover = (props: StepCardPopoverProps) => {
  const { cardReference } = useContext(StepCardContext);
  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const reference = props.useStepCardReference
    ? cardReference
    : isUndefined(props.referenceElement)
    ? referenceElement
    : props.referenceElement;
  const { styles, attributes } = usePopper(reference, popperElement, {
    placement: props.placement ?? 'bottom-end',
    modifiers: props.modifiers ?? [
      {
        name: 'offset',
        options: {
          offset: [14, 14],
        },
      },
    ],
  });

  const { children, isOpen, className, ...buttonProps } = props;
  return (
    <Popover>
      {({ open, close }) => {
        const isPopoverOpen = isUndefined(props.isOpen) ? open : props.isOpen;
        const closePopover = () => {
          props.onClose?.();
          close();
        };
        return (
          <>
            {!props.disablePopoverButton && (
              <Popover.Button
                ref={setReferenceElement}
                as={HeaderButton}
                {...buttonProps}
                selected={isPopoverOpen}
              />
            )}

            <Popover.Panel
              ref={setPopperElement}
              style={styles.popper}
              {...attributes.popper}
              className={twMerge('z-50', className)}
              static={!isUndefined(isOpen)}
            >
              <Transition
                show={isPopoverOpen}
                appear
                enter="transition-all duration-300"
                enterFrom="opacity-0 -translate-y-1"
                enterTo="opacity-100 translate-y-0 "
                leave="transition-all duration-300"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 -translate-y-1"
              >
                {isFunction(children)
                  ? children({
                      close: closePopover,
                      buttonReference: referenceElement,
                      panelReference: popperElement,
                      referenceElement: reference,
                    })
                  : children}
              </Transition>
            </Popover.Panel>
          </>
        );
      }}
    </Popover>
  );
};

export const StepCard = Object.assign(StepCardComponent, {
  Header: StepCardHeader,
  SubHeaderRow: StepCardSubHeaderRow,
  SubHeader: StepCardSubHeader,
  SubHeaderTitle: StepCardSubHeaderTitle,
  HeaderColumn: StepCardHeaderColumn,
  HeaderLeft: StepCardHeaderLeft,
  HeaderRight: StepCardHeaderRight,
  Icon: StepCardHeaderIcon,
  Image: StepCardHeaderImage,
  Superscript: StepCardHeaderSuperscript,
  Title: StepCardHeaderTitle,
  Subtitle: StepCardHeaderSubtitle,
  Button: HeaderButton,
  Body: StepCardBody,
  Footer: StepCardFooter,
  Popover: StepCardPopover,
  Context: StepCardContext,
});
